import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { attendantsService, Attendant } from '../services/attendantsService';

// Cores predefinidas para escolha
const PRESET_COLORS = [
  '#3B82F6', // Azul
  '#10B981', // Verde
  '#F59E0B', // Amarelo
  '#EF4444', // Vermelho
  '#8B5CF6', // Roxo
  '#EC4899', // Rosa
  '#06B6D4', // Ciano
  '#F97316', // Laranja
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

const Attendants: React.FC = () => {
  // Estados
  const [attendants, setAttendants] = useState<Attendant[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAttendant, setEditingAttendant] = useState<Attendant | null>(null);
  const [deletingAttendant, setDeletingAttendant] = useState<Attendant | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    color: '#3B82F6',
    is_active: true,
  });

  // Carregar atendentes
  const loadAttendants = async () => {
    try {
      setLoading(true);
      const data = await attendantsService.getAll();
      setAttendants(data);
    } catch (error) {
      console.error('Erro ao carregar atendentes:', error);
      showSnackbar('Erro ao carregar atendentes', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendants();
  }, []);

  // Handlers
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (attendant?: Attendant) => {
    if (attendant) {
      setEditingAttendant(attendant);
      setFormData({
        name: attendant.name,
        email: attendant.email || '',
        phone: attendant.phone || '',
        color: attendant.color,
        is_active: attendant.is_active,
      });
    } else {
      setEditingAttendant(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        color: '#3B82F6',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAttendant(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Nome é obrigatório', 'error');
      return;
    }

    try {
      setSaving(true);
      if (editingAttendant) {
        await attendantsService.update(editingAttendant.id, formData);
        showSnackbar('Atendente atualizado com sucesso', 'success');
      } else {
        await attendantsService.create(formData);
        showSnackbar('Atendente criado com sucesso', 'success');
      }
      handleCloseDialog();
      loadAttendants();
    } catch (error) {
      console.error('Erro ao salvar atendente:', error);
      showSnackbar('Erro ao salvar atendente', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingAttendant) return;

    try {
      setSaving(true);
      await attendantsService.delete(deletingAttendant.id);
      showSnackbar('Atendente desativado com sucesso', 'success');
      setDeleteDialogOpen(false);
      setDeletingAttendant(null);
      loadAttendants();
    } catch (error) {
      console.error('Erro ao excluir atendente:', error);
      showSnackbar('Erro ao excluir atendente', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (attendant: Attendant) => {
    try {
      await attendantsService.update(attendant.id, {
        is_active: !attendant.is_active,
      });
      showSnackbar(
        attendant.is_active ? 'Atendente desativado' : 'Atendente ativado',
        'success'
      );
      loadAttendants();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      showSnackbar('Erro ao alterar status', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          👤 Atendentes
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadAttendants} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Atendente
          </Button>
        </Box>
      </Box>

      {/* Tabela de Atendentes */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Atendente</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : attendants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhum atendente cadastrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              attendants.map((attendant) => (
                <TableRow key={attendant.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: attendant.color,
                          width: 40,
                          height: 40,
                        }}
                      >
                        {attendant.avatar_url ? (
                          <img
                            src={attendant.avatar_url}
                            alt={attendant.name}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          attendant.name.charAt(0).toUpperCase()
                        )}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="medium">
                          {attendant.name}
                        </Typography>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: attendant.color,
                            display: 'inline-block',
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {attendant.color}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{attendant.email || '-'}</TableCell>
                  <TableCell>{attendant.phone || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={attendant.is_active ? 'Ativo' : 'Inativo'}
                      color={attendant.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(attendant)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(attendant)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setDeletingAttendant(attendant);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Criar/Editar */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAttendant ? 'Editar Atendente' : 'Novo Atendente'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome *"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Telefone"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Cor de Identificação
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: color,
                      cursor: 'pointer',
                      border:
                        formData.color === color
                          ? '3px solid #000'
                          : '2px solid transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  />
                ))}
              </Box>
              <TextField
                label="Cor personalizada"
                value={formData.color}
                onChange={(e) =>
                  setFormData({ ...formData, color: e.target.value })
                }
                size="small"
                sx={{ mt: 1, width: 150 }}
              />
            </Box>
            {editingAttendant && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                }
                label="Ativo"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja realmente desativar o atendente{' '}
            <strong>{deletingAttendant?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            O atendente será desativado e não aparecerá mais nas opções de
            atribuição.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Desativar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Attendants;
